import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);   // Feb 2026 — 30-day token when checked
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Three states: 'loading' | 'ok' | 'notfound'. Only render the form when 'ok'.
  const [tenantState, setTenantState] = useState('loading');
  const [branding, setBranding] = useState({ name: '', tagline: '', logo: null });
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [searchParams] = useSearchParams();
  // Surface a helpful notice when the user was auto-logged-out due to idle timeout.
  const idleSignedOut = searchParams.get('reason') === 'idle';

  useEffect(() => {
    // Every legitimate login flow MUST come in through `/t/<slug>/login`.
    // A missing slug means someone landed here directly — bounce to marketing.
    if (!tenantSlug) {
      navigate('/', { replace: true });
      return;
    }
    setTenantState('loading');
    axios.get(`${API}/tenants/by-slug/${tenantSlug}`)
      .then(r => {
        const data = r.data || {};
        const name = (data.company_name || '').trim() || 'Vooxy';
        const logo = data.logo_url;
        const fullLogoUrl = logo ? (logo.startsWith('http') ? logo : `${BACKEND_URL}${logo}`) : null;
        setBranding({ name, tagline: 'powered by Vooxy', logo: fullLogoUrl });
        document.title = `${name} — CRM`;

        // ALWAYS rewrite the favicon links (never leave whatever the browser
        // has cached — some visitors still see the ancient "VOPA" icon
        // otherwise). Use the tenant logo when we have one, else the
        // versioned Vooxy default so the browser refetches.
        const iconHref = fullLogoUrl || '/favicon.svg?v=vooxy-cat-3';
        document.querySelectorAll("link[rel*='icon']").forEach(l => l.parentNode.removeChild(l));
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = iconHref;
        document.head.appendChild(link);
        const appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        appleLink.href = iconHref;
        document.head.appendChild(appleLink);
        setTenantState('ok');
      })
      .catch(() => {
        // Unknown workspace — show a proper "not found" page, DON'T fall back
        // to a default tenant's branding. That would let attackers enumerate
        // tenant slugs by simply loading the page.
        setTenantState('notfound');
      });
  }, [tenantSlug, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, rememberMe);

    if (result.success) {
      // After login, prefer the tenant slug from the JWT (baked in server-side).
      // CRITICAL: super-admins have tenant_slug=null in the JWT — we must NOT
      // fall back to the URL slug here, or they'd land in a tenant dashboard
      // instead of the /super-admin console.
      let jwtSlug = null;
      let isSuper = false;
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          jwtSlug = payload.tenant_slug ? payload.tenant_slug.toLowerCase() : null;
          isSuper = payload.is_super_admin === true;
        }
      } catch { /* ignore */ }

      if (isSuper) {
        navigate('/super-admin');
      } else {
        const slug = jwtSlug || tenantSlug || '';
        if (slug) {
          navigate(`/t/${slug}/dashboard`);
        } else {
          navigate('/');
        }
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Loading state — brief. We show a plain background to avoid a flash of
  // "Vooxy" branding before we know whether the tenant is valid.
  if (tenantState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F9F7]" data-testid="login-loading">
        <div className="text-[#3B5A54] text-sm">Loading workspace…</div>
      </div>
    );
  }

  // Workspace-not-found — deliberately generic. Doesn't reveal whether the
  // slug maps to a deactivated tenant or one that never existed.
  if (tenantState === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F9F7] px-6" data-testid="login-notfound">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-50 grid place-items-center">
            <X size={32} className="text-red-500" />
          </div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
            Workspace not found
          </h1>
          <p className="text-[#3B5A54] text-sm leading-relaxed mb-6">
            We couldn&apos;t find a workspace at{' '}
            <span className="font-mono text-[#00a69c]">vooxy.app/t/{tenantSlug}</span>. Double-check the URL your administrator sent you.
          </p>
          <p className="text-xs text-[#6D8E87]">
            Need help? <a href="mailto:sales@vooxy.app" className="text-[#00a69c] font-medium hover:underline">sales@vooxy.app</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1677611998429-1baa4371456b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdyZWVuJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzU3MTczNzZ8MA&ixlib=rb-4.1.0&q=85')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#00a69c]/90 to-[#007a72]/90 flex items-center justify-center">
          <div className="text-white text-center p-8">
            {branding.logo ? (
              <img src={branding.logo} alt={branding.name} className="w-24 h-24 object-contain mx-auto mb-4 rounded-2xl bg-white/15 p-2 backdrop-blur-sm" data-testid="login-company-logo" />
            ) : (
              <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-white/15 backdrop-blur-sm grid place-items-center" data-testid="login-company-initial">
                <span className="text-4xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                  {(branding.name || 'V').charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }} data-testid="login-company-name">{branding.name}</h1>
            <p className="text-sm mt-2 opacity-70">powered by Vooxy</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F4F9F7]">
        <div className="w-full max-w-md">
          <div className="medical-card p-8">
            <h2 className="text-3xl font-bold text-[#052A23] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="login-title">
              Welcome Back
            </h2>
            <p className="text-[#6D8E87] mb-6" data-testid="login-subtitle">Sign in to your account</p>

            {idleSignedOut && !error && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="idle-signed-out-notice">
                You were signed out after 30&nbsp;minutes of inactivity. Please log in again to continue.
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center justify-between" data-testid="login-error">
                <span>{error}</span>
                <X size={20} className="cursor-pointer" onClick={() => setError('')} />
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="login-form">
              <div className="mb-4">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2" htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="you@hospital.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="mb-6">
                <label className="block text-[#3B5A54] text-sm font-medium mb-2" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none transition"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>

              {/* Feb 2026 — Keep me signed in: extends JWT to 30 days.
                  Unchecked = default 8h. Users on shared machines should leave
                  it off; the tooltip explains this. */}
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-[#3B5A54]" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-[#D5E8E4] text-[#00a69c] focus:ring-2 focus:ring-[#00bfb3] accent-[#00a69c]"
                  data-testid="remember-me-checkbox"
                />
                Keep me signed in for 30 days
                <span
                  className="ml-1 text-[10px] text-[#6D8E87]"
                  title="Leave unchecked on shared or public computers."
                >(?)</span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#00a69c] text-white py-3 rounded-full font-medium hover:bg-[#00bfb3] transition-all duration-200 disabled:opacity-50"
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <p className="text-center text-sm text-[#6D8E87] mt-4">
              Don't have an account?{' '}
              <button
                onClick={() => navigate(tenantSlug ? `/t/${tenantSlug}/signup` : '/')}
                className="text-[#00a69c] font-medium hover:underline"
                data-testid="signup-link"
              >
                Sign Up
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
